import { Alert, Button, FormLabel, Icon, Modal, Text, TextArea } from "@oysterhr/ui"
import { useForm } from "react-hook-form"
import { yupResolver } from "@hookform/resolvers/yup"
import * as Yup from "yup"
import { AgreementDetailsFragment } from "../../../../gql/graphql"
import { useTranslation } from "react-i18next"

interface VoidEnvelopeConfirmationModalProps {
  agreements: AgreementDetailsFragment[]
  loading?: boolean
  isOpen?: boolean
  onConfirm: (reason: string) => void
  onClose: () => void
}

const validationSchema = Yup.object().shape({
  reasonForVoiding: Yup.string().required("Reason for voiding is required"),
})

type FormValues = {
  reasonForVoiding: string
}
export const VoidEnvelopeConfirmationModal = ({
  agreements,
  loading,
  isOpen,
  onConfirm,
  onClose,
}: VoidEnvelopeConfirmationModalProps) => {
  const { t } = useTranslation("Agreements", { keyPrefix: "voidEnvelope.modal" })
  const { t: tAgreementsNames } = useTranslation("Common")

  const {
    handleSubmit,
    register,
    reset,
    formState: { errors },
  } = useForm<FormValues>({
    defaultValues: {
      reasonForVoiding: "",
    },
    resolver: yupResolver(validationSchema),
  })

  const onSubmit = (values: FormValues) => {
    reset()
    onConfirm(values.reasonForVoiding)
  }

  return (
    <Modal isOpen={isOpen} onRequestClose={onClose}>
      <Modal.Header align="center">{t("header")}</Modal.Header>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Modal.Content className="space-y-4">
          <Alert variation="warning">
            <Alert.Icon />
            <Alert.Content>{t("alert")}</Alert.Content>
          </Alert>

          <div>
            <TextArea
              {...register("reasonForVoiding")}
              error={errors?.reasonForVoiding?.message}
              label={<FormLabel label={t("label")} assistiveText={t("assistiveText")} />}
            />
          </div>

          <div className="bg-gray-100 p-4">
            <Text className="font-semibold">{t("agreementsHeader")}</Text>
            <ul className="space-y-3 py-2.5">
              {agreements?.map(agreement => (
                <li key={agreement.id} className="flex flex-row items-center">
                  <Icon name="File" className="mr-2" />
                  <Text className="text-gray-400">
                    {tAgreementsNames(`agreementTypeEnum.${agreement.agreementType}`, {
                      ns: "Common",
                    })}
                  </Text>
                </li>
              ))}
            </ul>
          </div>
        </Modal.Content>
        <Modal.Footer>
          <Button variation="secondary" onClick={onClose}>
            {t("cancel")}
          </Button>
          <Button type="submit" disabled={loading} isLoading={loading}>
            {t("confirm")}
          </Button>
        </Modal.Footer>
      </form>
    </Modal>
  )
}
